import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import type { ContactDetail, ContactPriceDto, ContactRatingDto, PriceInput, RatingGrade } from '@tww/shared';
import { contractModeOf, contractStatus, fmtDate, fmtEur, fmtHa, fmtNum, ratingWarning, seasonLabel } from '@tww/shared';
import { api } from '../../api/client';
import { useCategoryDefs, useLookups } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { FilesTab } from '../../ui/FilesTab';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { ErrorState, FactCard, Facts, KpiLine, Note, Panel, RatingBadge, Skeleton, StatusPill } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { NumberInput, Select, TextArea, TextInput } from '../../ui/fields';
import { useToast } from '../../ui/Toast';
import { ReviewNote } from '../../ui/ReviewNote';
import { attrLabel } from './attributes';

/** Lesbare Namen der externen Kennungen */
const EXTERNAL_ID_LABELS: Record<string, string> = { fundraisingbox: 'FundraisingBox', partner_code: 'Partnercode' };
const externalIdLabel = (k: string) => EXTERNAL_ID_LABELS[k] ?? (k.charAt(0).toUpperCase() + k.slice(1)).replace(/_/g, ' ');

export function ContactDetailPage() {
  const { id } = useParams();
  const cid = Number(id);
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get('tab') === 'dateien' ? 'dateien' : 'uebersicht';
  const { can, me } = useAuth();
  const defs = useCategoryDefs();
  const lookups = useLookups();
  const q = useQuery({ queryKey: ['contact', cid], queryFn: () => api.get<ContactDetail>(`/contacts/${cid}`), enabled: Number.isFinite(cid) });
  const [rate, setRate] = useState(false);
  const [price, setPrice] = useState<ContactPriceDto | 'new' | null>(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const write = can('contacts', 'write');
  const mayRate = write && (me?.roles ?? []).some((r) => ['admin', 'forst', 'flaeche', 'projekt'].includes(r));

  const archive = useMutation({
    mutationFn: (restore: boolean) => api.post(`/contacts/${cid}/${restore ? 'restore' : 'archive'}`),
    onSuccess: (_d, restore) => {
      qc.invalidateQueries({ queryKey: ['contact', cid] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      setConfirmArchive(false);
      toast(restore ? 'Kontakt wiederhergestellt' : 'Kontakt archiviert');
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });

  if (q.isLoading) return <Skeleton rows={8} />;
  if (q.isError || !q.data) return <ErrorState error={q.error ?? new Error('Kontakt nicht gefunden')} retry={() => q.refetch()} />;
  const c = q.data;
  const s = c.summary;
  const isForester = c.categories.includes('forester');
  const isPartner = c.categories.includes('partner') || c.categories.includes('donor');
  // Flächen der Aktionen dieses Partners (areas_count zählt nur Flächen als Förster oder Waldbesitzer)
  const partnerAreas = new Set(c.actions.map((x) => x.area_id).filter((x): x is number => x != null)).size;
  const isOwner = c.categories.includes('owner');
  const warn = ratingWarning(c.rating_grade, c.rating_reason);
  const catName = (k: string) => defs.data?.find((d) => d.key === k)?.name ?? k;
  const nearbyLabel: Record<string, string> = { hotel: 'Hotels', wc: 'WC', guide: 'Pflanzlotsen', educator: 'Umweltpädagogik', supplier: 'Lieferanten' };
  const addr = [c.street, [c.zip, c.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') || (c.organisation ? [c.organisation.street, [c.organisation.zip, c.organisation.city].filter(Boolean).join(' ')].filter(Boolean).join(', ') : '');
  const setTab = (t: string) => {
    const n = new URLSearchParams(sp);
    if (t === 'uebersicht') n.delete('tab');
    else n.set('tab', t);
    setSp(n, { replace: true });
  };

  return (
    <>
      <div className="crumb">
        <Link to={`/kontakte${c.categories[0] ? `?f.category=${c.categories[0]}` : ''}`}>{c.categories[0] ? (defs.data?.find((d) => d.key === c.categories[0])?.name_plural ?? 'Kontakte') : 'Kontakte'}</Link>
      </div>
      <div className="head">
        <div className="title">
          <RatingBadge grade={c.rating_grade} size="lg" reason={c.rating_reason} at={c.rated_at} />
          <div>
            <h1>
              {c.display_name}
              {c.archived_at && <span className="pill muted" style={{ marginLeft: 10, verticalAlign: 'middle' }}>archiviert</span>}
            </h1>
            <div className="sub">
              {[c.organisation_name ? <Link key="o" className="link" to={`/kontakte/${c.organisation_id}`}>{c.organisation_name}</Link> : c.position, [c.state_label, c.region].filter(Boolean).join(', '), c.categories.map(catName).join(' · ')]
                .filter(Boolean)
                .map((x, i) => (
                  <span key={i}>
                    {i > 0 && ' · '}
                    {x}
                  </span>
                ))}
            </div>
          </div>
        </div>
        <div className="actions">
          {(c.mobile || c.phone) && (
            <a className="btn" href={`tel:${(c.mobile ?? c.phone ?? '').replace(/\s/g, '')}`}>
              <Icon name="phone" /> {c.mobile ?? c.phone}
            </a>
          )}
          {c.email && (
            <a className="btn" href={`mailto:${c.email}`}>
              <Icon name="mail" /> E-Mail
            </a>
          )}
          {isForester && can('actions', 'write') && (
            <Link className="btn primary" to={`/aktionen/neu?foerster=${c.id}`}>
              Neue Aktion
            </Link>
          )}
          {write && (
            <Link className="btn" to={`/kontakte/${c.id}/bearbeiten`}>
              <Icon name="edit" /> Bearbeiten
            </Link>
          )}
          {write && !c.archived_at && (
            <button className="btn" type="button" onClick={() => setConfirmArchive(true)}>
              <Icon name="archive" /> Archivieren
            </button>
          )}
          {write && c.archived_at && (
            <button className="btn" type="button" onClick={() => archive.mutate(true)}>
              Wiederherstellen
            </button>
          )}
        </div>
      </div>
      {warn && <Note kind="bad">{c.rating_reason?.trim() ? `${warn.replace(/[.!\s]+$/, '')}. Nicht ohne Rücksprache mit dem Vorstand erneut beauftragen.` : 'Bewertung C: nicht ohne Rücksprache mit dem Vorstand erneut beauftragen.'}</Note>}
      {c.merged_into && (
        <Note kind="info">
          Dieser Kontakt wurde mit einem anderen zusammengeführt. <Link className="link" to={`/kontakte/${c.merged_into}`}>Zum zusammengeführten Kontakt</Link>
        </Note>
      )}
      {c.review_needed && <ReviewNote entity="contacts" id={c.id} queryKey={['contact', cid]} canWrite={can('contacts', 'write')} />}

      <nav className="nav-tabs" aria-label="Akte">
        <button type="button" className={tab === 'uebersicht' ? 'active' : ''} onClick={() => setTab('uebersicht')}>
          Übersicht
        </button>
        <button type="button" className={tab === 'dateien' ? 'active' : ''} onClick={() => setTab('dateien')}>
          Dateien
        </button>
      </nav>

      {tab === 'dateien' ? (
        <div className="panel">
          <FilesTab entityType="contact" entityId={c.id} canWrite={write} ownLabel="Dateien dieses Kontakts" />
        </div>
      ) : (
        <>
          {isPartner ? (
            <KpiLine
              items={[
                { label: s.actions.count === 1 ? 'Aktion' : 'Aktionen', value: s.actions.count, detail: s.actions.count ? undefined : 'noch keine im System' },
                { label: 'Bäume', value: fmtNum(s.actions.trees) },
                { label: 'Hektar', value: fmtHa(s.actions.hectares), detail: partnerAreas ? `${partnerAreas} ${partnerAreas === 1 ? 'Fläche' : 'Flächen'}` : undefined },
                { label: 'Teilnehmende', value: fmtNum(s.actions.participants) },
              ]}
            />
          ) : (
            <Facts>
              <FactCard label="Letzter Baumpreis" value={s.last_price ? fmtEur(s.last_price.price) : '–'} detail={c.prices.length ? [...c.prices].reverse().map((p) => `${seasonLabel(p.season_code, p.year)}: ${fmtEur(p.price_per_tree)}`).join(' · ') : 'noch keine Vereinbarung'} />
              {(() => {
                const mode = contractModeOf(c.attributes);
                const st = contractStatus(mode, s.contract.year);
                const detail = mode === 'ohne Vertrag' ? 'wird nicht nachgefordert' : s.contract.exists ? `zuletzt ${s.contract.year}, als Vorlage nutzbar` : mode === 'Kooperationsvereinbarung' ? 'Vereinbarung noch nicht abgelegt' : 'beim nächsten Kontakt ansprechen';
                return <FactCard label={mode === 'Waldbesitzervertrag' ? 'Waldbesitzervertrag' : 'Vertrag'} value={mode === 'Waldbesitzervertrag' ? (s.contract.exists ? 'liegt vor' : 'fehlt') : mode} missing={st.missing} detail={detail} />;
              })()}
              <FactCard label="Pflanzbestätigungen" value={`${s.confirmations.done} von ${s.confirmations.total}`} missing={s.confirmations.missing.length > 0} detail={s.confirmations.missing.length ? `fehlt: ${s.confirmations.missing.map((m) => m.label).join(', ')}` : s.confirmations.total ? 'vollständig' : 'noch keine abgeschlossene Aktion'} />
              <FactCard label="Gemeinsame Aktionen" value={s.actions.count} detail={s.actions.count ? `${fmtHa(s.actions.hectares)} ha · ${fmtNum(s.actions.trees)} Bäume` : 'Noch keine gemeinsame Aktion'} />
            </Facts>
          )}

          <div className="grid g21">
            <div className="col">
              <Panel title="Aktionen mit diesem Kontakt">
                {c.actions.length ? (
                  <div className="tw">
                    <table>
                      <thead>
                        <tr>
                          <th>Aktion</th>
                          <th>Fläche</th>
                          <th className="num">Bäume</th>
                          <th>Stand</th>
                        </tr>
                      </thead>
                      <tbody>
                        {c.actions.map((a) => (
                          <tr key={a.id} className="row" onClick={() => nav(`/aktionen/${a.id}`)}>
                            <td>
                              <Link className="link" to={`/aktionen/${a.id}`}>
                                {a.label}
                              </Link>
                              <span className="sm">
                                {fmtDate(a.date)} · {a.type}
                              </span>
                            </td>
                            <td>{a.area_name ?? '–'}</td>
                            <td className="num">{fmtNum(a.trees)}</td>
                            <td>{a.after ? <StatusPill code={a.after.kind === 'secured' ? 'bestaetigt' : a.after.kind === 'confirmation_missing' ? 'warn' : a.after.kind === 'done' ? 'abgeschlossen' : 'abstimmung'} label={a.after.label} /> : <StatusPill code={a.status} label={a.status_label} />}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty">Noch keine gemeinsame Aktion.</div>
                )}
              </Panel>
              {isOwner && (
                <Panel title="Flächen">
                  {c.areas.length ? (
                    <div className="tw">
                      <table>
                        <thead>
                          <tr>
                            <th>Fläche</th>
                            <th>Region</th>
                            <th className="num">ha</th>
                            <th>Status</th>
                            <th>Förster</th>
                          </tr>
                        </thead>
                        <tbody>
                          {c.areas.map((a) => (
                            <tr key={a.id} className="row" onClick={() => nav(`/flaechen/${a.id}`)}>
                              <td>{a.name}</td>
                              <td>{a.region}</td>
                              <td className="num">{fmtHa(a.hectares)}</td>
                              <td>{a.status}</td>
                              <td>{a.forester_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty">Noch keine Fläche zugeordnet</div>
                  )}
                </Panel>
              )}
              <Panel title="Kontakt">
                <div className="pb">
                  <dl className="dl">
                    {c.kind === 'person' && c.salutation && <><dt>Anrede</dt><dd>{[c.salutation, c.title].filter(Boolean).join(' ')}</dd></>}
                    <dt>Telefon</dt><dd>{c.phone ?? '–'}</dd>
                    <dt>Mobil</dt><dd>{c.mobile ?? '–'}</dd>
                    <dt>E-Mail</dt><dd>{c.email ? <a className="link" href={`mailto:${c.email}`}>{c.email}</a> : '–'}</dd>
                    <dt>Adresse</dt><dd>{addr || '–'}{!c.street && c.organisation?.street && <span className="mut small"> (von {c.organisation.display_name})</span>}</dd>
                    {c.website && <><dt>Website</dt><dd><a className="link" href={c.website.startsWith('http') ? c.website : `https://${c.website}`} target="_blank" rel="noreferrer">{c.website}</a></dd></>}
                    {c.position && <><dt>Position</dt><dd>{c.position}</dd></>}
                    {c.organisation && <><dt>Organisation</dt><dd><Link className="link" to={`/kontakte/${c.organisation.id}`}>{c.organisation.display_name}</Link></dd></>}
                    {c.external_ids && Object.keys(c.external_ids).length > 0 && <><dt>Kennungen</dt><dd>{Object.entries(c.external_ids).map(([k, v]) => `${externalIdLabel(k)}: ${v}`).join(', ')}</dd></>}
                  </dl>
                </div>
              </Panel>
              {c.members.length > 0 && (
                <Panel title="Ansprechpersonen">
                  <div className="tw">
                    <table>
                      <tbody>
                        {c.members.map((m) => (
                          <tr key={m.id} className="row" onClick={() => nav(`/kontakte/${m.id}`)}>
                            <td style={{ width: 60 }}><RatingBadge grade={m.rating_grade} /></td>
                            <td><Link className="link" to={`/kontakte/${m.id}`}>{m.display_name}</Link>{m.position && <span className="sm">{m.position}</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Panel>
              )}
              {c.notes && (
                <Panel title="Notizen">
                  <div className="pb" style={{ whiteSpace: 'pre-wrap' }}>{c.notes}</div>
                </Panel>
              )}
            </div>
            <div className="col">
              <Panel
                title="Bewertung"
                action={
                  <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <RatingBadge grade={c.rating_grade} />
                    {mayRate && (
                      <button className="btn sm" type="button" onClick={() => setRate(true)}>
                        Neu bewerten
                      </button>
                    )}
                  </span>
                }
              >
                <div className="pb">
                  {c.ratings[0] ? (
                    <>
                      {c.ratings[0].reason || <span className="mut">ohne Begründung</span>}
                      <div className="mut small" style={{ marginTop: 8 }}>
                        {c.ratings[0].rated_by_name ?? 'unbekannt'}, {fmtDate(c.ratings[0].rated_at)}
                        {c.ratings.length > 1 && ` · ${c.ratings.length - 1} frühere`}
                      </div>
                    </>
                  ) : (
                    <span className="mut">Noch nicht bewertet</span>
                  )}
                </div>
              </Panel>
              {isForester && (
                <Panel
                  title="Baumpreis"
                  action={
                    write && (
                      <button className="btn sm" type="button" onClick={() => setPrice('new')}>
                        <Icon name="plus" /> Preis
                      </button>
                    )
                  }
                >
                  {c.prices.length ? (
                    <div className="tw">
                      <table>
                        <tbody>
                          {c.prices.map((p) => (
                            <tr key={p.id} className={write ? 'row' : ''} onClick={write ? () => setPrice(p) : undefined}>
                              <td>{seasonLabel(p.season_code, p.year)}</td>
                              <td className="num">{fmtEur(p.price_per_tree)}</td>
                              <td className="mut small">{{ protocol: 'Protokoll', contract: 'Vertrag', invoice: 'Rechnung', manual: 'manuell' }[p.source]}{p.note ? ` · ${p.note}` : ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty">noch keine Vereinbarung</div>
                  )}
                </Panel>
              )}
              {c.category_details.filter((cd) => (defs.data?.find((d) => d.key === cd.category_key)?.fields.length ?? 0) > 0).map((cd) => {
                const def = defs.data?.find((d) => d.key === cd.category_key);
                const rows = (def?.fields ?? []).map((f) => [f.label, attrLabel(f, cd.attributes[f.key], lookups.data)] as const).filter(([, v]) => v);
                return (
                  <Panel key={cd.category_key} title={cd.category_key === 'forester' ? 'Forstprofil' : `Profil ${cd.name}`}>
                    <div className="pb">
                      {rows.length ? (
                        <dl className="dl">
                          {rows.map(([l, v]) => (
                            <span key={l} style={{ display: 'contents' }}>
                              <dt>{l}</dt>
                              <dd>{v}</dd>
                            </span>
                          ))}
                          {cd.category_key === 'forester' && cd.attributes.organizes_fire === false && <><dt>Feuer</dt><dd>Schwedenfackeln mitnehmen</dd></>}
                        </dl>
                      ) : (
                        <span className="mut">Noch keine Angaben</span>
                      )}
                    </div>
                  </Panel>
                );
              })}
              {Object.keys(c.nearby).length > 0 && (
                <Panel title={`In der Region${c.region ? ` · ${c.region}` : ''}`}>
                  <div className="pb">
                    <dl className="dl">
                      {Object.entries(nearbyLabel).map(([k, l]) => {
                        const list = c.nearby[k];
                        if (!list?.length) return null;
                        return (
                          <span key={k} style={{ display: 'contents' }}>
                            <dt>{l}</dt>
                            <dd>
                              {k === 'guide' ? (
                                <>{list.length} ({list.filter((x) => x.rating_grade === 'A').length} mit A) · {list.map((x, i) => <span key={x.id}>{i > 0 && ', '}<Link className="link" to={`/kontakte/${x.id}`}>{x.display_name}</Link></span>)}</>
                              ) : (
                                list.map((x) => (
                                  <span key={x.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                                    <Link className="link" to={`/kontakte/${x.id}`}>{x.display_name}</Link> <RatingBadge grade={x.rating_grade} />
                                  </span>
                                ))
                              )}
                            </dd>
                          </span>
                        );
                      })}
                    </dl>
                  </div>
                </Panel>
              )}
            </div>
          </div>
        </>
      )}

      {rate && <RatingModal contact={c} onClose={() => setRate(false)} />}
      {price && <PriceModal contactId={c.id} price={price === 'new' ? null : price} onClose={() => setPrice(null)} />}
      {confirmArchive && (
        <ConfirmDialog
          title="Kontakt archivieren"
          text={<>„{c.display_name}“ bleibt in alten Aktionen sichtbar, erscheint aber nicht mehr in Auswahllisten. Gelöscht wird nichts.</>}
          confirmLabel="Archivieren"
          onConfirm={() => archive.mutate(false)}
          onClose={() => setConfirmArchive(false)}
          busy={archive.isPending}
        />
      )}
    </>
  );
}

function RatingModal({ contact, onClose }: { contact: ContactDetail; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [grade, setGrade] = useState<RatingGrade | ''>(contact.rating_grade ?? '');
  const [reason, setReason] = useState('');
  const m = useMutation({
    mutationFn: () => api.post<{ items: ContactRatingDto[] }>(`/contacts/${contact.id}/ratings`, { grade, reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contact', contact.id] });
      qc.invalidateQueries({ queryKey: ['contacts'] });
      toast('Bewertung gespeichert');
      onClose();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  return (
    <Modal
      title="Neu bewerten"
      subtitle={contact.display_name}
      onClose={onClose}
      footer={
        <>
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" disabled={!grade || m.isPending} onClick={() => m.mutate()}>
            Speichern
          </button>
        </>
      }
    >
      <div className="form one">
        <div className="field">
          <label>Bewertung</label>
          <div className="seg" role="radiogroup">
            {(['A', 'B', 'C'] as const).map((g) => (
              <button key={g} type="button" role="radio" aria-checked={grade === g} className={grade === g ? 'on' : ''} onClick={() => setGrade(g)}>
                <RatingBadge grade={g} /> {{ A: 'super', B: 'ok', C: 'nicht wieder' }[g]}
              </button>
            ))}
          </div>
        </div>
        <TextArea label="Begründung" value={reason} onChange={setReason} rows={3} hint="Was lief gut oder schlecht? Hilft der Vertretung." />
      </div>
      {contact.ratings.length > 0 && (
        <details style={{ marginTop: 14 }}>
          <summary className="mut small" style={{ cursor: 'pointer' }}>
            Bisherige Bewertungen ({contact.ratings.length})
          </summary>
          <ul className="list" style={{ border: '1px solid var(--line)', borderRadius: 8, marginTop: 8 }}>
            {contact.ratings.map((r) => (
              <li key={r.id}>
                <RatingBadge grade={r.grade} />
                <span>
                  {r.reason || <span className="mut">ohne Begründung</span>}
                  <span className="sm" style={{ display: 'block', color: 'var(--muted)' }}>
                    {r.rated_by_name ?? 'unbekannt'}, {fmtDate(r.rated_at)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </Modal>
  );
}

function PriceModal({ contactId, price, onClose }: { contactId: number; price: ContactPriceDto | null; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState<PriceInput>({ year: price?.year ?? new Date().getFullYear(), season_code: price?.season_code ?? null, price_per_tree: price?.price_per_tree ?? 0, source: price?.source ?? 'manual', note: price?.note ?? '' });
  const done = () => {
    qc.invalidateQueries({ queryKey: ['contact', contactId] });
    qc.invalidateQueries({ queryKey: ['contacts'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => (price ? api.patch(`/contacts/${contactId}/prices/${price.id}`, f) : api.post(`/contacts/${contactId}/prices`, f)),
    onSuccess: () => {
      toast('Preis gespeichert');
      done();
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const [confirmDel, setConfirmDel] = useState(false);
  const del = useMutation({
    mutationFn: () => api.delete(`/contacts/${contactId}/prices/${price!.id}`),
    onSuccess: () => {
      toast('Preis entfernt');
      done();
    },
    onError: (e: Error) => {
      setConfirmDel(false);
      toast(e.message, 'error');
    },
  });
  if (confirmDel && price)
    return (
      <ConfirmDialog
        title="Baumpreis entfernen"
        text={<>Den Preis {fmtEur(price.price_per_tree)} für {seasonLabel(price.season_code, price.year)} entfernen?</>}
        confirmLabel="Entfernen"
        danger
        onConfirm={() => del.mutate()}
        onClose={() => setConfirmDel(false)}
        busy={del.isPending}
      />
    );
  return (
    <Modal
      title={price ? 'Baumpreis bearbeiten' : 'Baumpreis eintragen'}
      subtitle="Maximal übernommener Betrag pro Baum, brutto"
      onClose={onClose}
      footer={
        <>
          {price && (
            <button className="btn danger" type="button" onClick={() => setConfirmDel(true)} style={{ marginRight: 'auto' }}>
              Entfernen
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" disabled={!f.price_per_tree || save.isPending} onClick={() => save.mutate()}>
            Speichern
          </button>
        </>
      }
    >
      <div className="form">
        <NumberInput label="Preis pro Baum (€)" decimals={2} value={f.price_per_tree} onChange={(v) => setF({ ...f, price_per_tree: v ?? 0 })} required />
        <NumberInput label="Jahr" value={f.year} onChange={(v) => setF({ ...f, year: v ?? new Date().getFullYear() })} />
        <Select label="Saison" value={f.season_code ?? ''} onChange={(v) => setF({ ...f, season_code: (v || null) as PriceInput['season_code'] })} options={[{ value: 'F', label: 'Frühjahr' }, { value: 'S', label: 'Sommer' }, { value: 'H', label: 'Herbst' }]} />
        <Select label="Quelle" value={f.source} onChange={(v) => setF({ ...f, source: v as PriceInput['source'] })} options={[{ value: 'manual', label: 'manuell' }, { value: 'protocol', label: 'Begehungsprotokoll' }, { value: 'contract', label: 'Vertrag' }, { value: 'invoice', label: 'Rechnung' }]} />
        <TextInput full label="Notiz" value={f.note ?? ''} onChange={(v) => setF({ ...f, note: v })} />
      </div>
    </Modal>
  );
}
